import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Search, TrendingUp, BookOpen, Briefcase, BarChart3,
  ChevronRight, Loader2, ChevronDown, ChevronUp
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { INVESTORS, STYLE_TAGS } from '../data/investorsData';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PIE_COLORS = ['#00e5a0','#4f8eff','#a78bfa','#f59e0b','#ef4444','#06b6d4','#ec4899','#84cc16','#f97316','#6366f1'];

const titleCase = (s) => s?.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) || '';
const fmtShares = (n) => {
  if (!n || n === 0) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return n.toLocaleString('fr-FR');
};

// ── Tooltip ──
const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1a1d27', border: '1px solid #00e5a0', borderRadius: 8, padding: '8px 12px' }}>
      <p className="text-white text-sm font-bold">{payload[0].name}</p>
      <p className="text-cap-text-secondary text-xs">{payload[0].value}%</p>
    </div>
  );
};

// ── Investor Card ──
const InvestorCard = ({ inv, badge, photo, stats, txCounts, onClick }) => {
  const imgSrc = inv.localImage || photo;
  const buys = txCounts?.buys || 0;
  const sells = txCounts?.sells || 0;
  const posCount = stats?.holdings_count;
  return (
  <div onClick={onClick}
    className="bg-[#141720] border border-white/5 rounded-2xl p-5 cursor-pointer hover:border-[#00e5a0]/30 transition-all group"
    data-testid={`investor-card-${inv.id}`}>
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3">
        {imgSrc ? (
          <img src={imgSrc} alt={inv.name} className="w-16 h-16 rounded-full object-cover border border-white/10" />
        ) : (
          <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${inv.gradient} flex items-center justify-center text-lg font-display font-bold text-white border border-white/10`}>
            {inv.initials}
          </div>
        )}
        <div>
          <h3 className="font-display text-base font-bold text-white group-hover:text-[#00e5a0] transition-colors">
            {inv.name}
            {inv.deceased && <span className="text-cap-text-muted text-xs ml-1 font-normal align-super">+</span>}
            {inv.retired && <span className="text-cap-text-muted text-[10px] ml-1.5 font-normal">Retraité</span>}
          </h3>
          <p className="text-cap-text-muted text-xs">{inv.fund}</p>
          <p className="text-cap-text-muted text-xs italic">{inv.style}</p>
        </div>
      </div>
      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#00e5a0]/15 text-[#00e5a0]">{badge || '—'}</span>
    </div>
    {/* Stats row */}
    <div className="flex items-center gap-2.5 flex-wrap mb-2">
      {posCount > 0 && !inv.retired && !inv.deceased && (
        <span className="text-[11px] text-cap-text-secondary font-mono" data-testid={`positions-${inv.id}`}>
          {posCount} positions
        </span>
      )}
      {buys > 0 && (
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#00e5a0]/10 text-[#00e5a0]" data-testid={`buys-${inv.id}`}>
          +{buys}
        </span>
      )}
      {sells > 0 && (
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500/10 text-red-400" data-testid={`sells-${inv.id}`}>
          -{sells}
        </span>
      )}
    </div>
    {/* Bottom row */}
    <div className="flex items-center justify-between">
      {inv.deceased ? (
        <span className="text-cap-text-muted text-[11px] italic">Décédé</span>
      ) : inv.retired ? (
        <span className="text-cap-text-muted text-[11px] italic">Données historiques</span>
      ) : <span />}
      <ChevronRight className="w-4 h-4 text-cap-text-muted group-hover:text-[#00e5a0] transition-colors" />
    </div>
  </div>
);
};

// ── Tendances Tab (real SEC data) ──
const TendancesTab = ({ photos }) => {
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/sec/trends`)
      .then(r => r.json())
      .then(data => { setTrends(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const investorMap = useMemo(() => {
    const m = {};
    INVESTORS.forEach(inv => { m[inv.id] = inv; });
    return m;
  }, []);

  const InvestorAvatars = ({ investorIds }) => (
    <div className="flex items-center gap-1">
      {(investorIds || []).slice(0, 6).map(id => {
        const inv = investorMap[id];
        if (!inv) return null;
        const src = inv.localImage || photos[id];
        return (
          <div key={id} className="w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold border border-white/10 overflow-hidden"
            title={inv.name}
            style={src ? {} : { background: 'rgba(255,255,255,0.05)' }}>
            {src ? <img src={src} alt={inv.initials} className="w-full h-full object-cover" />
              : <span className="text-cap-text-secondary">{inv.initials}</span>}
          </div>
        );
      })}
    </div>
  );

  const Section = ({ title, color, data, showName }) => (
    <div className="space-y-3">
      <h3 className={`font-display font-bold text-sm ${color}`}>{title}</h3>
      {(data || []).map((d, i) => (
        <div key={d.ticker || d.name || i} className="bg-[#141720] border border-white/5 rounded-xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-mono font-bold text-white text-sm">{d.ticker || '?'}</span>
            {showName && <span className="text-cap-text-secondary text-xs truncate max-w-[140px]">{titleCase(d.name)}</span>}
            <span className="text-cap-text-muted text-xs">{d.count} investisseur{d.count > 1 ? 's' : ''}</span>
          </div>
          <InvestorAvatars investorIds={d.investors} />
        </div>
      ))}
      {(!data || data.length === 0) && <p className="text-cap-text-muted text-xs italic">Aucune donnée disponible</p>}
    </div>
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-[#00e5a0] animate-spin mb-3" />
        <p className="text-cap-text-secondary text-sm">Analyse des filings SEC de tous les investisseurs...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="tendances-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Section title="Les plus achetées ce trimestre" color="text-[#00e5a0]" data={trends?.most_bought} showName />
        <Section title="Les plus vendues ce trimestre" color="text-red-400" data={trends?.most_sold} showName />
      </div>
      {/* Consensus section */}
      <div className="space-y-3" data-testid="consensus-section">
        <h3 className="font-display font-bold text-sm text-amber-400">Les plus détenues (consensus)</h3>
        <p className="text-cap-text-muted text-xs mb-2">Actions actuellement en portefeuille chez le plus grand nombre d'investisseurs.</p>
        {(trends?.most_held || []).map((d, i) => (
          <div key={d.ticker || i} className="bg-[#141720] border border-white/5 rounded-xl px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="font-mono font-bold text-white text-sm">{d.ticker || '?'}</span>
              <span className="text-cap-text-secondary text-xs truncate max-w-[140px]">{titleCase(d.name)}</span>
              <span className="text-cap-text-muted text-xs">{d.count} investisseur{d.count > 1 ? 's' : ''}</span>
            </div>
            <InvestorAvatars investorIds={d.investors} />
          </div>
        ))}
        {(!trends?.most_held || trends.most_held.length === 0) && (
          <p className="text-cap-text-muted text-xs italic">Aucune donnée disponible</p>
        )}
      </div>
      <p className="text-cap-text-muted text-xs">
        Tendances calculées en comparant les deux derniers filings 13F de chaque investisseur actif.
      </p>
    </div>
  );
};

// ──────────────── MAIN COMPONENT ────────────────
const SuperInvestorsPage = () => {
  const { user, token } = useAuth();
  const userId = user?.id;
  const [search, setSearch] = useState('');
  const [styleFilter, setStyleFilter] = useState('Tous');
  const [homeTab, setHomeTab] = useState('investisseurs');
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('portfolio');
  const [secData, setSecData] = useState(null);
  const [secLoading, setSecLoading] = useState(false);
  const [quarters, setQuarters] = useState({});
  const [photos, setPhotos] = useState({});
  const [stats, setStats] = useState({});
  const [txData, setTxData] = useState(null);
  const [cardTxCounts, setCardTxCounts] = useState({});
  const [showAllHoldings, setShowAllHoldings] = useState(false);
  const [userTickers, setUserTickers] = useState(new Set());

  // User portfolio tickers — fetched from db.user_data (migrated from localStorage)
  useEffect(() => {
    if (!userId || !token) return;
    fetch(`${API_URL}/api/store/patrimoine`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : {})
      .then(data => {
        const positions = data?.positions || [];
        const tickers = new Set(
          positions.map(p => p.ticker?.split('.')[0]?.split(':').pop()?.toUpperCase()).filter(Boolean)
        );
        setUserTickers(tickers);
      })
      .catch(() => {});
  }, [userId, token]);

  // Fetch quarters + photos + stats + trends on mount
  useEffect(() => {
    fetch(`${API_URL}/api/sec/all-quarters`).then(r => r.json()).then(setQuarters).catch(() => {});
    fetch(`${API_URL}/api/investors/photos`).then(r => r.json()).then(setPhotos).catch(() => {});
    fetch(`${API_URL}/api/sec/all-stats`).then(r => r.json()).then(setStats).catch(() => {});
    // Fetch trends to get real buy/sell counts per investor for cards
    fetch(`${API_URL}/api/sec/trends`).then(r => r.json()).then(data => {
      const counts = {};
      [...(data.most_bought || []), ...(data.most_sold || [])].forEach(d => {
        (d.investors || []).forEach(id => {
          if (!counts[id]) counts[id] = { buys: 0, sells: 0 };
        });
      });
      (data.most_bought || []).forEach(d => {
        (d.investors || []).forEach(id => { counts[id] = counts[id] || { buys: 0, sells: 0 }; counts[id].buys++; });
      });
      (data.most_sold || []).forEach(d => {
        (d.investors || []).forEach(id => { counts[id] = counts[id] || { buys: 0, sells: 0 }; counts[id].sells++; });
      });
      setCardTxCounts(counts);
    }).catch(() => {});
  }, []);

  const selectInvestor = useCallback(async (inv) => {
    setSelected(inv);
    setTab('portfolio');
    setSecData(null);
    setTxData(null);
    setShowAllHoldings(false);
    if (inv.retired) return;
    setSecLoading(true);
    try {
      const [holdingsRes, txRes] = await Promise.all([
        fetch(`${API_URL}/api/sec/holdings/${inv.id}`),
        fetch(`${API_URL}/api/sec/transactions/${inv.id}`),
      ]);
      const holdingsData = await holdingsRes.json();
      const txResult = await txRes.json();
      if (holdingsData.holdings?.length > 0) setSecData(holdingsData);
      if (txResult.transactions?.length > 0) setTxData(txResult);
    } catch (e) {
      console.error('SEC fetch failed', e);
    } finally {
      setSecLoading(false);
    }
  }, []);

  const activeHoldings = secData?.holdings || [];
  const activeBadge = secData?.quarter || quarters[selected?.id]?.quarter || '—';
  const filingDate = secData?.filing_date;

  const filtered = useMemo(() => {
    let list = INVESTORS;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(i => i.name.toLowerCase().includes(q) || i.fund.toLowerCase().includes(q));
    }
    if (styleFilter !== 'Tous') {
      list = list.filter(i => i.styleTag === styleFilter);
    }
    return list;
  }, [search, styleFilter]);

  const displayHoldings = showAllHoldings ? activeHoldings : activeHoldings.slice(0, 20);

  // Check if a SEC holding symbol is in user's portfolio
  const isInPortfolio = useCallback((secName) => {
    if (userTickers.size === 0) return false;
    const upper = secName?.toUpperCase() || '';
    for (const ticker of userTickers) {
      if (upper.includes(ticker) || ticker.includes(upper.split(' ')[0])) return true;
    }
    return false;
  }, [userTickers]);

  // ── DETAIL VIEW ──
  if (selected) {
    const pieData = activeHoldings.slice(0, 10).map(h => ({ name: h.ticker || titleCase(h.symbol), value: h.weight }));
    const tabs = [
      { key: 'portfolio', label: 'Portefeuille', icon: <BarChart3 className="w-4 h-4" /> },
      { key: 'transactions', label: 'Transactions', icon: <TrendingUp className="w-4 h-4" /> },
      { key: 'philosophy', label: 'Philosophie', icon: <BookOpen className="w-4 h-4" /> },
    ];

    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="investor-detail">
        <button onClick={() => { setSelected(null); setSecData(null); }}
          className="flex items-center gap-2 text-cap-text-secondary hover:text-white transition-colors mb-6 text-sm" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        {/* Header */}
        <div className="flex items-center gap-5 mb-8">
          {(selected.localImage || photos[selected.id]) ? (
            <img src={selected.localImage || photos[selected.id]} alt={selected.name} className="w-20 h-20 rounded-full object-cover border border-white/10" />
          ) : (
            <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${selected.gradient} flex items-center justify-center text-2xl font-display font-bold text-white border border-white/10`}>
              {selected.initials}
            </div>
          )}
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-white">{selected.name}</h1>
              <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#00e5a0]/15 text-[#00e5a0]">{activeBadge}</span>
            </div>
            <p className="text-cap-text-secondary text-sm">{selected.fund}</p>
            <p className="text-cap-text-muted text-xs mt-1">{selected.style}{selected.aum && ` · AUM ${selected.aum}`}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-white/5 pb-px">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${tab === t.key ? 'bg-[#141720] text-[#00e5a0] border-b-2 border-[#00e5a0]' : 'text-cap-text-muted hover:text-white'}`}
              data-testid={`tab-${t.key}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* PORTFOLIO TAB */}
        {tab === 'portfolio' && (
          <div className="space-y-6">
            {secLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-[#00e5a0] animate-spin mb-3" />
                <p className="text-cap-text-secondary text-sm">Chargement des données SEC EDGAR...</p>
              </div>
            ) : selected.retired ? (
              <div className="bg-[#141720] border border-white/5 rounded-2xl p-6 text-center">
                <Briefcase className="w-12 h-12 text-cap-text-muted mx-auto mb-3" />
                <p className="text-white font-display font-bold mb-1">Données historiques uniquement</p>
                <p className="text-cap-text-secondary text-sm">Peter Lynch a géré le Fidelity Magellan Fund de 1977 à 1990 avec un rendement annualisé de 29.2%. Il est retraité depuis 1990.</p>
              </div>
            ) : activeHoldings.length > 0 ? (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                  {/* Pie Chart */}
                  <div className="lg:col-span-2 bg-[#141720] border border-white/5 rounded-2xl p-5">
                    <h3 className="text-white font-display font-bold text-sm mb-1">Répartition Top 10</h3>
                    <p className="text-cap-text-muted text-[10px] mb-3">{activeHoldings.length} positions au total</p>
                    <div className="h-[260px]">
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={50} paddingAngle={2} strokeWidth={0}>
                            {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                          </Pie>
                          <Tooltip content={<PieTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {pieData.map((d, i) => (
                        <div key={d.name} className="flex items-center gap-1.5 text-xs text-cap-text-secondary">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                          {d.name}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Holdings Table */}
                  <div className="lg:col-span-3 space-y-4">
                    <div className="bg-[#141720] border border-white/5 rounded-2xl overflow-hidden">
                      <table className="w-full text-left" data-testid="holdings-table">
                        <thead>
                          <tr className="border-b border-white/5 text-cap-text-muted text-xs uppercase">
                            <th className="py-3 px-4">Ticker</th>
                            <th className="py-3 px-4">Entreprise</th>
                            <th className="py-3 px-4 text-right">Poids %</th>
                            <th className="py-3 px-4 text-right">Valeur</th>
                            <th className="py-3 px-4 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayHoldings.map((h, i) => (
                            <tr key={h.symbol || i} className={`border-b border-white/5 ${i % 2 === 0 ? 'bg-white/[0.01]' : ''}`}>
                              <td className="py-2.5 px-4 font-mono text-sm text-[#00e5a0] font-bold flex items-center gap-1.5">
                                {h.ticker || titleCase(h.symbol)}
                                {isInPortfolio(h.ticker || h.symbol || h.name) && (
                                  <span title="Dans ton portefeuille" className="text-[#00e5a0]"><Briefcase className="w-3.5 h-3.5" /></span>
                                )}
                              </td>
                              <td className="py-2.5 px-4 text-sm text-white">{titleCase(h.name)}</td>
                              <td className="py-2.5 px-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <div className="w-16 h-1.5 bg-white/5 rounded-full overflow-hidden">
                                    <div className="h-full rounded-full bg-[#00e5a0]" style={{ width: `${Math.min(h.weight * 2, 100)}%` }} />
                                  </div>
                                  <span className="text-white font-mono text-sm w-14 text-right">{h.weight}%</span>
                                </div>
                              </td>
                              <td className="py-2.5 px-4 text-right text-cap-text-secondary text-sm font-mono">{h.value}</td>
                              <td className="py-2.5 px-4 text-right text-cap-text-secondary text-sm font-mono">{fmtShares(h.shares)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {activeHoldings.length > 20 && (
                      <button onClick={() => setShowAllHoldings(!showAllHoldings)}
                        className="flex items-center gap-2 text-xs text-[#00e5a0] hover:underline mx-auto">
                        {showAllHoldings ? <><ChevronUp className="w-3.5 h-3.5" /> Réduire</> : <><ChevronDown className="w-3.5 h-3.5" /> Voir les {activeHoldings.length} positions</>}
                      </button>
                    )}
                    {filingDate && (
                      <p className="text-cap-text-muted text-xs leading-relaxed" data-testid="sec-disclaimer">
                        Données issues des formulaires 13F déposés auprès de la SEC. Mise à jour trimestrielle avec un délai réglementaire de 45 jours. Dernière mise à jour : {new Date(filingDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}.
                      </p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-[#141720] border border-white/5 rounded-2xl p-8 text-center">
                <p className="text-cap-text-muted text-sm">Aucune donnée de portefeuille disponible pour cet investisseur.</p>
              </div>
            )}
          </div>
        )}

        {/* TRANSACTIONS TAB */}
        {tab === 'transactions' && (
          <div className="space-y-4" data-testid="transactions-tab">
            {txData ? (
              <>
                <div className="flex items-center gap-4 mb-2">
                  <span className="text-xs bg-[#00e5a0]/10 text-[#00e5a0] font-bold px-2.5 py-1 rounded">{txData.buys} achats</span>
                  <span className="text-xs bg-red-500/10 text-red-400 font-bold px-2.5 py-1 rounded">{txData.sells} ventes</span>
                  <span className="text-cap-text-muted text-xs">{txData.prev_quarter} → {txData.latest_quarter}</span>
                </div>
                <div className="bg-[#141720] border border-white/5 rounded-2xl overflow-hidden">
                  <table className="w-full text-left" data-testid="tx-table">
                    <thead>
                      <tr className="border-b border-white/5 text-cap-text-muted text-xs uppercase">
                        <th className="py-3 px-4">Type</th>
                        <th className="py-3 px-4">Ticker</th>
                        <th className="py-3 px-4">Entreprise</th>
                        <th className="py-3 px-4 text-right">Actions</th>
                        <th className="py-3 px-4 text-right">% Portefeuille</th>
                        <th className="py-3 px-4 text-right">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {txData.transactions.map((tx, i) => (
                        <tr key={i} className={`border-b border-white/5 ${i % 2 === 0 ? 'bg-white/[0.01]' : ''}`} data-testid={`tx-${i}`}>
                          <td className="py-2.5 px-4">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${tx.type === 'buy' ? 'bg-[#00e5a0]/15 text-[#00e5a0]' : 'bg-red-500/15 text-red-400'}`}>
                              {tx.type === 'buy' ? 'ACHAT' : 'VENTE'}
                            </span>
                          </td>
                          <td className="py-2.5 px-4 font-mono text-sm text-[#00e5a0] font-bold">{tx.ticker}</td>
                          <td className="py-2.5 px-4 text-sm text-white">
                            <div>{titleCase(tx.name)}</div>
                            <div className="text-cap-text-muted text-[10px]">{tx.action}</div>
                          </td>
                          <td className="py-2.5 px-4 text-right text-cap-text-secondary text-sm font-mono">{fmtShares(tx.shares)}</td>
                          <td className="py-2.5 px-4 text-right text-cap-text-secondary text-sm font-mono">{tx.pct > 0 ? `${tx.pct}%` : '—'}</td>
                          <td className="py-2.5 px-4 text-right text-cap-text-muted text-xs">{tx.date ? new Date(tx.date).toLocaleDateString('fr-FR') : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-cap-text-muted text-xs">
                  Mouvements calculés en comparant les filings 13F du {txData.prev_quarter} et du {txData.latest_quarter}.
                </p>
              </>
            ) : secLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-8 h-8 text-[#00e5a0] animate-spin mb-3" />
                <p className="text-cap-text-secondary text-sm">Analyse des derniers filings SEC...</p>
              </div>
            ) : (
              <div className="bg-[#141720] border border-white/5 rounded-2xl p-8 text-center">
                <p className="text-cap-text-muted text-sm">Aucun mouvement détecté entre les deux derniers filings.</p>
              </div>
            )}
          </div>
        )}

        {/* PHILOSOPHY TAB */}
        {tab === 'philosophy' && (
          <div className="space-y-6">
            <div className="border-l-4 border-[#00e5a0] bg-[#00e5a0]/5 rounded-r-xl px-6 py-5">
              <p className="text-white font-display text-lg italic leading-relaxed">"{selected.quote}"</p>
              <p className="text-cap-text-muted text-sm mt-2">— {selected.name}</p>
            </div>
            <div className="bg-[#141720] border border-white/5 rounded-2xl p-6 space-y-4">
              <h3 className="text-white font-display font-bold text-sm">Style d'investissement</h3>
              {selected.philosophy?.map((p, i) => (
                <p key={i} className="text-cap-text-secondary text-sm leading-relaxed">{p}</p>
              ))}
            </div>
            <div className="bg-[#141720] border border-white/5 rounded-2xl p-6">
              <h3 className="text-white font-display font-bold text-sm mb-3">Critères de sélection typiques</h3>
              <div className="flex flex-wrap gap-2">
                {selected.criteria?.map((c, i) => (
                  <span key={i} className="px-3 py-1.5 text-xs rounded-full bg-[#00e5a0]/10 text-[#00e5a0] border border-[#00e5a0]/20">{c}</span>
                ))}
              </div>
            </div>
            <div className="bg-[#141720] border border-white/5 rounded-2xl p-6">
              <h3 className="text-white font-display font-bold text-sm mb-3">Livres & ressources recommandés</h3>
              <ul className="space-y-2">
                {selected.books?.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <BookOpen className="w-4 h-4 text-[#00e5a0] mt-0.5 flex-shrink-0" />
                    <span className="text-cap-text-secondary">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <p className="text-cap-text-muted text-xs text-center mt-8">
          Source : SEC Form 13F{secData ? ' (données réelles SEC EDGAR)' : ''}
        </p>
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="super-investors-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-2">Super-Investisseurs</h1>
        <p className="text-cap-text-secondary text-base max-w-2xl mb-5">
          Suivez les portefeuilles des plus grands investisseurs mondiaux
        </p>
        <div className="relative max-w-md mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cap-text-muted" />
          <Input placeholder="Rechercher par nom ou fonds..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-10 bg-[#141720] border-white/10 text-white placeholder:text-cap-text-muted" data-testid="search-investors" />
        </div>
        {/* Style filters */}
        <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="style-filters">
          {STYLE_TAGS.map(s => (
            <button key={s} onClick={() => setStyleFilter(s)}
              className={`px-3 py-1.5 text-xs rounded-full transition-colors ${styleFilter === s ? 'bg-[#00e5a0]/15 text-[#00e5a0] font-bold' : 'bg-white/5 text-cap-text-muted hover:text-white'}`}
              data-testid={`style-${s}`}>
              {s}
            </button>
          ))}
        </div>
        {/* Home tabs */}
        <div className="flex gap-4 border-b border-white/5 pb-px" data-testid="home-tabs">
          {[{ key: 'investisseurs', label: 'Investisseurs' }, { key: 'tendances', label: 'Tendances' }].map(t => (
            <button key={t.key} onClick={() => setHomeTab(t.key)}
              className={`pb-2 text-sm font-medium transition-colors ${homeTab === t.key ? 'text-[#00e5a0] border-b-2 border-[#00e5a0]' : 'text-cap-text-muted hover:text-white'}`}
              data-testid={`hometab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {homeTab === 'investisseurs' ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map(inv => (
              <InvestorCard key={inv.id} inv={inv}
                badge={quarters[inv.id]?.quarter || '...'}
                photo={photos[inv.id]}
                stats={stats[inv.id]}
                txCounts={cardTxCounts[inv.id]}
                onClick={() => selectInvestor(inv)} />
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-12">
              <p className="text-cap-text-muted">Aucun investisseur ne correspond à votre recherche.</p>
            </div>
          )}
        </>
      ) : (
        <TendancesTab photos={photos} />
      )}

      <p className="text-cap-text-muted text-xs text-center mt-8">
        Données basées sur les formulaires 13F déposés auprès de la SEC
      </p>
    </div>
  );
};

export default SuperInvestorsPage;
